import axios from "axios";
import { Project } from "../../../shared/types";
import { buildApiUrl } from "../config/api";

export interface ProjectsResponse {
  projects: Project[];
  page: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface CreateProjectInput {
  name: string;
  description?: string;
  color?: string;
  billable?: boolean;
  currency?: string; // ISO currency code (EUR, USD, GBP, CHF, JPY)
  hourlyRate?: number; // in project's currency, optional for billable projects (default rate)
  budget?: number; // in project's currency or hours per day, optional for billable projects
  budgetType?: "monetary" | "hours"; // type of budget: monetary or daily hours
  budgetHourLimit?: number; // in minutes, tolerance for hourly budget compliance (default: 60 minutes = 1 hour)
  budgetExcludedDays?: number[]; // days excluded from hourly budget warnings (0=Sun…6=Sat), default [0,6]
  locations?: { name: string; hourlyRate?: number }[]; // now contains location objects with optional hourly rates
  type?: "personal" | "shared";
  sharedUsers?: string[];
}

export interface UpdateProjectInput {
  name?: string;
  description?: string;
  color?: string;
  status?: "active" | "archived";
  billable?: boolean;
  currency?: string; // ISO currency code (EUR, USD, GBP, CHF, JPY)
  hourlyRate?: number; // in project's currency, optional for billable projects (default rate)
  budget?: number; // in project's currency or hours per day, optional for billable projects
  budgetType?: "monetary" | "hours"; // type of budget: monetary or daily hours
  budgetHourLimit?: number; // in minutes, tolerance for hourly budget compliance (default: 60 minutes = 1 hour)
  budgetExcludedDays?: number[]; // days excluded from hourly budget warnings (0=Sun…6=Sat), default [0,6]
  locations?: { name: string; hourlyRate?: number }[]; // now contains location objects with optional hourly rates
  type?: "personal" | "shared";
  sharedUsers?: string[];
}

class ProjectService {
  private baseUrl = buildApiUrl("projects");

  /**
   * Get all projects for the current user
   */
  async getProjects(
    status?: "active" | "archived" | "all",
    page?: number,
    limit?: number
  ): Promise<ProjectsResponse> {
    const params: Record<string, string | number> = {};
    if (status === "all") {
      params.includeInactive = "true";
    } else if (status) {
      params.status = status;
    }
    if (page) params.page = page;
    if (limit) params.limit = limit;

    const response = await axios.get(this.baseUrl, { params });
    return response.data;
  }

  /**
   * Get a specific project
   */
  async getProject(id: string): Promise<{ project: Project }> {
    const response = await axios.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new project
   */
  async createProject(
    input: CreateProjectInput
  ): Promise<{ project: Project; message: string }> {
    const response = await axios.post(this.baseUrl, input);
    return response.data;
  }

  /**
   * Update a project
   */
  async updateProject(
    id: string,
    updates: UpdateProjectInput
  ): Promise<{ project: Project; message: string }> {
    const response = await axios.put(`${this.baseUrl}/${id}`, updates);
    return response.data;
  }

  /**
   * Delete a project
   */
  async deleteProject(
    id: string
  ): Promise<{ message: string; project?: Project }> {
    const response = await axios.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get active projects only
   */
  async getActiveProjects(): Promise<Project[]> {
    const response = await this.getProjects("active");
    return response.projects;
  }

  /**
   * Restore an archived project (set status to active)
   */
  async restoreProject(
    id: string
  ): Promise<{ project: Project; message: string }> {
    const response = await axios.put(`${this.baseUrl}/${id}`, {
      status: "active",
    });
    return response.data;
  }

  /**
   * Get locations that have associated time entries for a project
   */
  async getLocationsWithEntries(id: string): Promise<{ locations: string[] }> {
    const response = await axios.get(`${this.baseUrl}/${id}/locations-with-entries`);
    return response.data;
  }
}

export const projectService = new ProjectService();
